import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import VerifiedAnalyticsBadge from "../components/VerifiedAnalyticsBadge";
import { Instagram, ExternalLink, Award, Newspaper, Briefcase, Users, Quote, MapPin } from "lucide-react";
import BrandLogo from "../components/BrandLogo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PublicKit() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const isPrint = params.get("print") === "1";
  const isMinimal = params.get("view") === "minimal";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/public/kit/${slug}`).then(({ data }) => setData(data)).catch(() => setError("Media kit not found"));
  }, [slug]);

  // Auto-trigger print after the page renders fully in print mode.
  useEffect(() => {
    if (isPrint && data) {
      const t = setTimeout(() => window.print(), 1200);
      return () => clearTimeout(t);
    }
  }, [isPrint, data]);

  if (error) return <div className="min-h-screen bg-bg flex items-center justify-center text-ink-muted" data-testid="public-kit-error">{error}</div>;
  if (!data) return <div className="min-h-screen bg-bg flex items-center justify-center label-eyebrow">Loading…</div>;

  if (isMinimal) return <MinimalView slug={slug} data={data} />;

  const { kit, profile, editorial, has_og_image, owner_id, owner_plan, social_connections } = data;
  const photos = editorial?.photos_present || {};
  const hasPhoto = (slot) => !!photos[slot];
  const photoUrl = (slot) => `${BACKEND}/api/media-kit/photo/${owner_id}/${slot}`;
  const career = editorial?.career_highlights || [];
  const honours = editorial?.honours || [];
  const coverage = editorial?.media_coverage || [];
  const partnerships = editorial?.past_partnerships || [];
  const demo = editorial?.audience_demographics || {};
  const pull = editorial?.pull_quote || "";

  return (
    <div className={`min-h-screen bg-bg text-white ${isPrint ? "print-mode" : ""}`} data-testid="public-kit">
      <PrintStyles />
      {!isPrint && (
        <header className="border-b border-line sticky top-0 bg-bg/95 backdrop-blur z-10 no-print">
          <div className="max-w-6xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
            <div className="flex items-center gap-2.5">
              <BrandLogo variant="wordmark" size={26} showLink={false} />
              <span className="font-heading text-lg uppercase tracking-tight text-ink-muted">Media Kit</span>
            </div>
            <div className="flex items-center gap-3">
              <a href={`?view=minimal`} className="text-[10px] uppercase tracking-widest text-ink-muted hover:text-white" data-testid="view-toggle-minimal">Minimal view</a>
              <a href="/" className="text-xs uppercase tracking-widest text-ink-muted hover:text-white">Build your own →</a>
            </div>
          </div>
        </header>
      )}

      {/* === SECTION 1: HERO === */}
      <section className="relative" data-testid="public-hero">
        {hasPhoto("hero") ? (
          <div className="relative h-[70vh] min-h-[480px] max-h-[800px] overflow-hidden">
            <img src={photoUrl("hero")} alt={profile.full_name} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-bg via-bg/40 to-bg/10" />
            <div className="absolute bottom-0 left-0 right-0 max-w-6xl mx-auto px-6 md:px-12 py-12">
              <div className="flex items-center gap-3 flex-wrap mb-3">
                <div className="label-eyebrow text-volt">Sponsor-ready media kit</div>
                <VerifiedAnalyticsBadge connections={social_connections} plan={owner_plan} variant="public" hideManual />
              </div>
              <h1 className="font-heading text-5xl md:text-8xl uppercase tracking-tighter leading-[0.92]" data-testid="public-kit-name">{profile.full_name || "Athlete"}</h1>
              <div className="font-mono text-xs md:text-sm text-ink-muted mt-4 uppercase tracking-[0.2em]">
                {[profile.sport, profile.level, profile.location].filter(Boolean).join(" · ")}
              </div>
            </div>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto px-6 md:px-12 py-20">
            <div className="flex items-center gap-3 flex-wrap mb-3">
              <div className="label-eyebrow text-volt">Sponsor-ready media kit</div>
              <VerifiedAnalyticsBadge connections={social_connections} plan={owner_plan} variant="public" hideManual />
            </div>
            <h1 className="font-heading text-5xl md:text-8xl uppercase tracking-tighter leading-[0.92]" data-testid="public-kit-name">{profile.full_name || "Athlete"}</h1>
            <div className="font-mono text-xs md:text-sm text-ink-muted mt-4 uppercase tracking-[0.2em]">
              {[profile.sport, profile.level, profile.location].filter(Boolean).join(" · ")}
            </div>
            {has_og_image && (
              <div className="mt-10 border border-line">
                <img src={`${BACKEND}/api/og/kit/${slug}`} alt={`${profile.full_name} card`} className="w-full" />
              </div>
            )}
          </div>
        )}
      </section>

      {/* === SECTION 2: ABOUT + PORTRAIT + PULL QUOTE === */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 border-t border-line">
        <div className="grid md:grid-cols-5 gap-8 md:gap-12 items-start">
          <div className="md:col-span-3 space-y-6">
            <div className="label-eyebrow">About {profile.full_name?.split(" ")[0] || "the athlete"}</div>
            {profile.personal_story && (
              <p className="text-lg leading-relaxed text-white" data-testid="public-personal-story">{profile.personal_story}</p>
            )}
            <p className="text-base leading-relaxed text-ink-muted" data-testid="public-bio">{kit?.athlete_bio || kit?.professional_bio}</p>
            {pull && (
              <blockquote className="border-l-4 border-volt pl-5 py-2 mt-8" data-testid="public-pullquote">
                <Quote className="w-5 h-5 text-volt mb-2" />
                <p className="font-heading text-2xl md:text-3xl uppercase tracking-tight leading-tight italic">{pull}</p>
              </blockquote>
            )}
          </div>
          <div className="md:col-span-2">
            {hasPhoto("portrait") ? (
              <img src={photoUrl("portrait")} alt={`${profile.full_name} portrait`} className="w-full aspect-[3/4] object-cover border border-line" data-testid="public-portrait" />
            ) : (
              <div className="w-full aspect-[3/4] border border-line bg-bg-elev flex items-center justify-center">
                <div className="font-heading text-6xl text-ink-dim opacity-30">{(profile.full_name || "A").slice(0, 1).toUpperCase()}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* === SECTION 3: CAREER + HONOURS + COVERAGE === */}
      {(career.length > 0 || honours.length > 0 || coverage.length > 0) && (
        <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 border-t border-line" data-testid="public-career-section">
          <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-tighter mb-10">Career & accolades</h2>
          <div className="grid md:grid-cols-2 gap-10">
            {/* Career */}
            {career.length > 0 && (
              <div data-testid="public-career-list">
                <div className="label-eyebrow mb-4 flex items-center gap-2"><Award className="w-3.5 h-3.5" /> Career highlights</div>
                <div className="space-y-4">
                  {career.map((c, i) => (
                    <div key={i} className="border-l-2 border-volt pl-4 pb-2">
                      <div className="font-heading text-lg uppercase tracking-tight">{c.club}</div>
                      <div className="text-xs text-ink-muted font-mono mt-0.5">{c.period}{c.role ? ` · ${c.role}` : ""}</div>
                      {(c.appearances || c.goals) && (
                        <div className="flex gap-6 mt-2 text-sm">
                          {c.appearances != null && <div><span className="text-volt font-mono">{c.appearances}</span> <span className="text-ink-muted text-xs">apps</span></div>}
                          {c.goals != null && <div><span className="text-volt font-mono">{c.goals}</span> <span className="text-ink-muted text-xs">goals</span></div>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-10">
              {/* Honours */}
              {honours.length > 0 && (
                <div data-testid="public-honours-list">
                  <div className="label-eyebrow mb-4 flex items-center gap-2"><Award className="w-3.5 h-3.5" /> Honours</div>
                  <ul className="space-y-2">
                    {honours.map((h, i) => (
                      <li key={i} className="text-sm flex gap-3"><span className="text-volt font-mono">·</span><span>{h}</span></li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Media coverage */}
              {coverage.length > 0 && (
                <div data-testid="public-coverage-list">
                  <div className="label-eyebrow mb-4 flex items-center gap-2"><Newspaper className="w-3.5 h-3.5" /> Featured in</div>
                  <div className="space-y-3">
                    {coverage.map((m, i) => (
                      <div key={i} className="border border-line p-4 hover:border-volt/50 transition-colors">
                        <div className="font-heading text-base uppercase tracking-tight">{m.publication}</div>
                        {m.headline && <div className="text-sm text-ink-muted mt-1 italic">"{m.headline}"</div>}
                        {m.url && <a href={m.url} target="_blank" rel="noreferrer noopener" className="text-xs text-volt mt-2 inline-flex items-center gap-1 hover:underline">Read <ExternalLink className="w-3 h-3" /></a>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* === GALLERY === */}
      {(hasPhoto("gallery_1") || hasPhoto("gallery_2") || hasPhoto("gallery_3") || hasPhoto("gallery_4")) && (
        <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 border-t border-line" data-testid="public-gallery">
          <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-tighter mb-10">In action</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {["gallery_1", "gallery_2", "gallery_3", "gallery_4"].map(slot => (
              hasPhoto(slot) ? (
                <img key={slot} src={photoUrl(slot)} alt={`${profile.full_name} ${slot}`} className="w-full aspect-square object-cover border border-line" data-testid={`public-${slot}`} />
              ) : null
            ))}
          </div>
        </section>
      )}

      {/* === SECTION 4: AUDIENCE + PARTNERSHIPS + PACKAGES === */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 border-t border-line">
        <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-tighter mb-10">Audience & partnerships</h2>

        {(demo.follower_count || demo.avg_impressions || demo.avg_reach || demo.engagement_rate_pct != null) && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-12" data-testid="public-demo-stats">
            {demo.follower_count != null && <Stat label="Followers" value={fmtNum(demo.follower_count)} />}
            {demo.avg_impressions != null && <Stat label="Avg impressions" value={fmtNum(demo.avg_impressions)} />}
            {demo.avg_reach != null && <Stat label="Avg reach" value={fmtNum(demo.avg_reach)} />}
            {demo.engagement_rate_pct != null && <Stat label="Engagement" value={`${demo.engagement_rate_pct}%`} />}
          </div>
        )}

        {(demo.female_pct != null || (demo.top_countries && demo.top_countries.length > 0)) && (
          <div className="grid md:grid-cols-2 gap-10 mb-12">
            {demo.female_pct != null && (
              <div data-testid="public-gender-split">
                <div className="label-eyebrow mb-4">Gender split</div>
                <div className="flex items-center gap-6">
                  <DonutPct pct={demo.female_pct} label="Female" />
                  <DonutPct pct={100 - demo.female_pct} label="Male" />
                </div>
              </div>
            )}
            {demo.top_countries && demo.top_countries.length > 0 && (
              <div data-testid="public-countries">
                <div className="label-eyebrow mb-4 flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> Top geographies</div>
                <div className="space-y-2">
                  {demo.top_countries.filter(c => c.country).map((c, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="font-mono text-xs uppercase w-12">{c.country}</div>
                      <div className="flex-1 h-1.5 bg-bg-elev overflow-hidden">
                        <div className="h-full bg-volt" style={{ width: `${c.pct || 0}%` }} />
                      </div>
                      <div className="font-mono text-xs text-volt w-12 text-right">{c.pct || 0}%</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {kit?.audience_summary && (
          <p className="text-sm text-ink-muted leading-relaxed mb-12 max-w-3xl" data-testid="public-audience-summary">{kit.audience_summary}</p>
        )}

        {/* Past partnerships */}
        {partnerships.length > 0 && (
          <div className="mb-12" data-testid="public-partnerships">
            <div className="label-eyebrow mb-4 flex items-center gap-2"><Briefcase className="w-3.5 h-3.5" /> Past partnerships</div>
            <div className="flex flex-wrap gap-2">
              {partnerships.map((p, i) => (
                <div key={i} className="border border-line px-4 py-2.5">
                  <div className="font-heading text-sm uppercase tracking-tight">{p.brand_name}</div>
                  {p.year && <div className="text-[10px] text-ink-muted font-mono mt-0.5">{p.year}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sponsorship packages from kit */}
        {(kit?.partnership_packages || kit?.sponsorship_packages || []).length > 0 && (
          <div data-testid="public-packages">
            <div className="label-eyebrow mb-4">Sponsorship packages</div>
            <div className="grid md:grid-cols-3 gap-3">
              {(kit?.partnership_packages || kit?.sponsorship_packages || []).map((p, i) => (
                <div key={p.name || i} className={`p-6 border ${i === 1 ? "border-volt bg-bg-elev" : "border-line bg-bg-surface"}`}>
                  <div className="font-heading text-2xl uppercase">{p.name}</div>
                  <div className="font-mono text-xs text-volt mt-1">{p.price_range}</div>
                  <ul className="mt-4 space-y-2 text-sm">
                    {(p.deliverables || []).map((d, j) => <li key={j} className="text-ink-muted">— {d}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* === CONTACT === */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 border-t border-line" data-testid="public-contact">
        <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-tighter mb-6">Let's work together</h2>
        <div className="flex flex-wrap gap-3 items-center text-sm">
          {profile.instagram_url && <SocialLink href={profile.instagram_url} label="Instagram" />}
          {profile.tiktok_url && <SocialLink href={profile.tiktok_url} label="TikTok" />}
          {profile.youtube_url && <SocialLink href={profile.youtube_url} label="YouTube" />}
          {profile.linkedin_url && <SocialLink href={profile.linkedin_url} label="LinkedIn" />}
        </div>
        {kit?.closing_paragraph && (
          <p className="text-base italic text-ink-muted mt-8 max-w-3xl" data-testid="public-closing">{kit.closing_paragraph}</p>
        )}
      </section>

      <footer className="border-t border-line py-8 text-center text-xs text-ink-dim no-print">
        Built with <a href="/" className="text-volt hover:underline">PITCHD</a> — the AI operating system for athlete commercial growth.
      </footer>
    </div>
  );
}

/* ---------- Print mode + minimal view helpers ---------- */

function PrintStyles() {
  return (
    <style>{`
      @media print {
        @page { size: A4; margin: 0; }
        html, body { background: #0A0A0A !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .no-print { display: none !important; }
        section { break-inside: avoid; page-break-inside: avoid; }
        .print-mode section + section { break-before: page; page-break-before: always; }
        .print-mode .sticky { position: static !important; }
        a { text-decoration: none !important; }
      }
    `}</style>
  );
}

function MinimalView({ slug, data }) {
  const { kit, profile, editorial } = data;
  const honours = editorial?.honours || [];
  const career = editorial?.career_highlights || [];
  const partnerships = editorial?.past_partnerships || [];
  return (
    <div className="min-h-screen bg-bg text-white" data-testid="public-kit-minimal">
      <header className="border-b border-line">
        <div className="max-w-3xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2.5">
            <BrandLogo variant="wordmark" size={26} showLink={false} />
            <span className="font-heading text-lg uppercase tracking-tight text-ink-muted">Media Kit</span>
          </div>
          <a href={`/kit/${slug}`} className="text-[10px] uppercase tracking-widest text-ink-muted hover:text-white" data-testid="view-toggle-editorial">Editorial view</a>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-16 space-y-10">
        <div>
          <div className="label-eyebrow">Sponsor-ready media kit</div>
          <h1 className="font-heading text-4xl md:text-6xl uppercase tracking-tighter mt-2">{profile.full_name}</h1>
          <div className="font-mono text-xs text-ink-muted mt-3 uppercase tracking-[0.18em]">
            {[profile.sport, profile.level, profile.location].filter(Boolean).join(" · ")}
          </div>
        </div>
        {(kit?.athlete_bio || kit?.professional_bio) && (
          <section>
            <div className="label-eyebrow mb-3">About</div>
            <p className="text-base leading-relaxed text-ink-muted">{kit.athlete_bio || kit.professional_bio}</p>
          </section>
        )}
        {career.length > 0 && (
          <section>
            <div className="label-eyebrow mb-3">Career highlights</div>
            <ul className="space-y-2 text-sm">
              {career.map((c, i) => (
                <li key={i}><span className="text-volt">·</span> <span className="font-semibold">{c.club}</span> <span className="text-ink-muted">— {c.period}{c.appearances != null ? ` · ${c.appearances} apps` : ""}{c.goals != null ? ` · ${c.goals} goals` : ""}</span></li>
              ))}
            </ul>
          </section>
        )}
        {honours.length > 0 && (
          <section>
            <div className="label-eyebrow mb-3">Honours</div>
            <ul className="space-y-1 text-sm">{honours.map((h, i) => <li key={i}><span className="text-volt">·</span> {h}</li>)}</ul>
          </section>
        )}
        {partnerships.length > 0 && (
          <section>
            <div className="label-eyebrow mb-3">Past partnerships</div>
            <div className="flex flex-wrap gap-2">
              {partnerships.map((p, i) => <span key={i} className="border border-line px-3 py-1.5 text-xs uppercase tracking-widest">{p.brand_name}</span>)}
            </div>
          </section>
        )}
        <section>
          <div className="label-eyebrow mb-3">Contact</div>
          <div className="flex flex-wrap gap-2">
            {profile.instagram_url && <a href={profile.instagram_url} className="text-xs uppercase tracking-widest text-volt hover:underline" target="_blank" rel="noreferrer">Instagram</a>}
            {profile.tiktok_url && <a href={profile.tiktok_url} className="text-xs uppercase tracking-widest text-volt hover:underline" target="_blank" rel="noreferrer">TikTok</a>}
            {profile.youtube_url && <a href={profile.youtube_url} className="text-xs uppercase tracking-widest text-volt hover:underline" target="_blank" rel="noreferrer">YouTube</a>}
            {profile.linkedin_url && <a href={profile.linkedin_url} className="text-xs uppercase tracking-widest text-volt hover:underline" target="_blank" rel="noreferrer">LinkedIn</a>}
          </div>
        </section>
      </main>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-line p-5">
      <div className="font-heading text-3xl md:text-4xl text-volt tracking-tight">{value}</div>
      <div className="label-eyebrow mt-2 text-[10px]">{label}</div>
    </div>
  );
}

function DonutPct({ pct, label }) {
  const r = 28;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;
  return (
    <div className="flex flex-col items-center">
      <svg width="70" height="70" className="-rotate-90">
        <circle cx="35" cy="35" r={r} fill="none" stroke="#2A2A2A" strokeWidth="6" />
        <circle cx="35" cy="35" r={r} fill="none" stroke="#E2FF31" strokeWidth="6" strokeDasharray={`${dash} ${c}`} strokeLinecap="butt" />
      </svg>
      <div className="font-mono text-lg text-volt mt-1">{pct}%</div>
      <div className="label-eyebrow text-[9px]">{label}</div>
    </div>
  );
}

function SocialLink({ href, label }) {
  return (
    <a href={href} target="_blank" rel="noreferrer noopener" className="border border-line px-4 py-2.5 inline-flex items-center gap-2 hover:border-volt hover:text-volt transition-colors text-xs uppercase tracking-widest">
      {label} <ExternalLink className="w-3 h-3" />
    </a>
  );
}

function fmtNum(n) {
  if (n == null) return "";
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K`;
  return String(n);
}
